import * as THREE from "three";

import type { LoomNativeHybridAsset } from "./loomNativeHybridAsset";
import { LOOM_INTERACTION_HEAVY_OVERLAY } from "./loomViewerInteractionQuality";
export { deriveVisiblePickTargets, isLoomObjectEffectivelyVisible, loomPickTargetEvidenceEnabled,
  resolveLoomViewportPickAtCanvasPoint, resolveLoomViewportPickNodeId } from "./loomViewportPicking";
export type { LoomPickTarget } from "./loomViewportPicking";

export interface LoomViewerDisplayPolicy {
  displayMode: "accepted-extraction" | "edited-preview";
  showAcceptedExtraction: boolean;
  staleExtractionVisible: false;
}

export interface LoomAnalyticPreviewSurface {
  baseValue: number;
  deltaDisplay: number;
  deltaJacobian: number;
  faces: Uint32Array;
  fuselageSeamVertexIndices: Uint32Array;
  parameterId: string;
  positions: Float32Array;
  targetValue: number;
  wingSeamVertexIndices: Uint32Array;
}

export interface LoomCadStudioRig {
  grid: THREE.GridHelper;
  lights: readonly THREE.Light[];
}

export function createIndexedGeometry(
  positions: Float32Array,
  faces: Uint32Array,
): THREE.BufferGeometry {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setIndex(new THREE.BufferAttribute(faces, 1));
  geometry.computeVertexNormals();
  return geometry;
}

export function addLoomCadStudioRig(
  scene: THREE.Scene,
  gridCenterColor: string,
  gridColor: string,
): LoomCadStudioRig {
  const hemisphere = new THREE.HemisphereLight("#dcecff", "#34434d", 0.5);
  hemisphere.name = "Loom studio ambient fill";
  const key = new THREE.DirectionalLight("#fffdf6", 2.4);
  key.name = "Loom studio key";
  key.position.set(-9, -12, 16);
  const fill = new THREE.DirectionalLight("#b9dbff", 0.46);
  fill.name = "Loom studio fill";
  fill.position.set(10, -1, 7);
  const rim = new THREE.DirectionalLight("#ffd6a6", 1.08);
  rim.name = "Loom studio rim";
  rim.position.set(7, 11, 13);
  const lights = [hemisphere, key, fill, rim] as const;
  scene.add(...lights);

  const grid = new THREE.GridHelper(18, 24, gridCenterColor, gridColor);
  grid.name = "Loom subdued reference grid";
  grid.renderOrder = -2;
  grid.rotation.x = Math.PI / 2;
  const materials = Array.isArray(grid.material) ? grid.material : [grid.material];
  materials.forEach((material) => {
    material.depthWrite = false;
    material.opacity = 0.2;
    material.transparent = true;
    material.toneMapped = false;
  });
  scene.add(grid);
  return { grid, lights };
}

export function placeLoomCadGroundGrid(grid: THREE.GridHelper, bounds: THREE.Box3): void {
  const size = bounds.getSize(new THREE.Vector3());
  const span = Math.max(size.x, size.y, size.z, 1);
  grid.position.z = bounds.min.z - Math.max(span * 0.035, 0.05);
}

export function loomCameraViewSignature(
  camera: THREE.PerspectiveCamera,
  target: THREE.Vector3,
): string {
  const encode = (values: readonly number[]) => values.map((value) => value.toFixed(6)).join(":");
  return `p:${encode(camera.position.toArray())}|q:${encode(camera.quaternion.toArray())}|t:${encode(target.toArray())}|z:${camera.zoom.toFixed(6)}`;
}

export function loomCameraMotionSignature(
  camera: THREE.PerspectiveCamera,
  target: THREE.Vector3,
): string {
  return [...camera.position.toArray(), ...target.toArray()].map((value) => value.toFixed(3)).join(":");
}

export function loomCameraFitRequired(
  appliedRequest: number | null,
  requestedFit: number,
): boolean {
  return appliedRequest === null || appliedRequest !== requestedFit;
}

export function syncLoomCadGroundGridVisibility(
  grid: THREE.GridHelper,
  camera: THREE.PerspectiveCamera,
): "visible" | "hidden-below-plane" {
  grid.visible = camera.position.z >= grid.position.z;
  return grid.visible ? "visible" : "hidden-below-plane";
}

function viewerToken(name: string, fallback: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
}

function loopPositions(positions: Float32Array, indices: Uint32Array): Float32Array {
  const values = new Float32Array((indices.length + (indices.length > 0 ? 1 : 0)) * 3);
  for (let index = 0; index < indices.length; index += 1) {
    const source = (indices[index] ?? 0) * 3;
    values.set(positions.subarray(source, source + 3), index * 3);
  }
  if (indices.length > 0) values.set(values.subarray(0, 3), indices.length * 3);
  return values;
}

export function addSharedSeams(
  group: THREE.Group,
  positions: Float32Array,
  seamRings: readonly Uint32Array[],
): void {
  seamRings.forEach((indices, index) => {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(loopPositions(positions, indices), 3));
    const line = new THREE.Line(geometry, new THREE.LineBasicMaterial({
      color: viewerToken("--fc-warning", "#e39c35"), depthTest: true, linewidth: 2,
    }));
    line.name = `Shared conforming seam ${index + 1}`;
    line.renderOrder = 8;
    group.add(line);
  });
}

export function addTessellation(
  group: THREE.Group,
  geometry: THREE.BufferGeometry,
  name = "Exact-surface triangulation",
): THREE.LineSegments {
  geometry.computeBoundingSphere();
  const normalOffset = Math.max(geometry.boundingSphere?.radius ?? 1, 1) * 0.00035;
  const surfacePositions = geometry.getAttribute("position");
  const surfaceNormals = geometry.getAttribute("normal");
  const lifted = geometry.clone();
  const liftedPositions = new Float32Array(surfacePositions.count * 3);
  for (let vertex = 0; vertex < surfacePositions.count; vertex += 1) {
    for (let axis = 0; axis < 3; axis += 1) {
      liftedPositions[vertex * 3 + axis] = surfacePositions.getComponent(vertex, axis)
        + surfaceNormals.getComponent(vertex, axis) * normalOffset;
    }
  }
  lifted.setAttribute("position", new THREE.BufferAttribute(liftedPositions, 3));
  const wire = new THREE.LineSegments(
    new THREE.WireframeGeometry(lifted),
    new THREE.LineBasicMaterial({
      color: viewerToken("--fc-ink-3", "#485764"),
      depthTest: true,
      depthWrite: false,
      opacity: 0.42,
      toneMapped: false,
      transparent: true,
    }),
  );
  lifted.dispose();
  wire.name = name;
  wire.renderOrder = 3;
  wire.userData.loomNormalOffset = normalOffset;
  wire.userData[LOOM_INTERACTION_HEAVY_OVERLAY] = true;
  group.add(wire);
  return wire;
}

function sampledSegments(
  segments: Uint32Array,
  include: (segmentIndex: number) => boolean,
): Uint32Array {
  const values: number[] = [];
  for (let segmentIndex = 0; segmentIndex < segments.length / 2; segmentIndex += 1) {
    if (!include(segmentIndex)) continue;
    values.push(segments[2 * segmentIndex] ?? 0, segments[2 * segmentIndex + 1] ?? 0);
  }
  return new Uint32Array(values);
}

function addParameterLineFamily(
  group: THREE.Group,
  positions: Float32Array,
  normals: Float32Array,
  segments: Uint32Array,
  color: string,
  name: string,
): void {
  const values = new Float32Array(segments.length * 3);
  for (let index = 0; index < segments.length; index += 1) {
    const vertex = segments[index] ?? 0;
    const source = vertex * 3;
    const target = index * 3;
    values[target] = (positions[source] ?? 0) + (normals[source] ?? 0) * 0.002;
    values[target + 1] = (positions[source + 1] ?? 0) + (normals[source + 1] ?? 0) * 0.002;
    values[target + 2] = (positions[source + 2] ?? 0) + (normals[source + 2] ?? 0) * 0.002;
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(values, 3));
  const lines = new THREE.LineSegments(
    geometry,
    new THREE.LineBasicMaterial({ color, depthTest: true, depthWrite: false }),
  );
  lines.name = name;
  lines.renderOrder = 4;
  group.add(lines);
}

export function addParameterization(group: THREE.Group, asset: LoomNativeHybridAsset): void {
  const normalGeometry = createIndexedGeometry(asset.surface.positions, asset.surface.faces);
  const normals = normalGeometry.getAttribute("normal").array as Float32Array;
  const wingVCount = Math.max(2, asset.header.panel.trailingEdgeCount + 1);
  const wingUCount = Math.max(1, asset.surface.parameterLines.wingChordwiseU.length / 2 / wingVCount);
  const nX = Math.max(2, Math.round(asset.header.resolution.n_x ?? 161));
  const nTheta = Math.max(3, Math.round(asset.header.resolution.n_theta ?? 121));
  const wingUStride = Math.max(1, Math.ceil(wingUCount / 28));
  const bodyXStride = Math.max(1, Math.ceil(nX / 24));
  const bodyThetaStride = Math.max(1, Math.ceil(nTheta / 24));

  addParameterLineFamily(group, asset.surface.positions, normals, asset.surface.parameterLines.wingChordwiseU,
    viewerToken("--fc-info", "#2368a2"), "Wing chordwise u parameter lines");
  addParameterLineFamily(group, asset.surface.positions, normals, sampledSegments(
    asset.surface.parameterLines.wingSpanwiseV,
    (segmentIndex) => Math.floor(segmentIndex / Math.max(1, wingVCount - 1)) % wingUStride === 0,
  ), viewerToken("--fc-primary-strong", "#195f43"), "Wing spanwise v parameter lines");
  addParameterLineFamily(group, asset.surface.positions, normals, sampledSegments(
    asset.surface.parameterLines.fuselageCircumferentialTheta,
    (segmentIndex) => segmentIndex % nX % bodyXStride === 0,
  ), viewerToken("--fc-warning", "#e39c35"), "Fuselage circumferential theta parameter lines");
  addParameterLineFamily(group, asset.surface.positions, normals, sampledSegments(
    asset.surface.parameterLines.fuselageAxialX,
    (segmentIndex) => Math.floor(segmentIndex / Math.max(1, nX - 1)) % bodyThetaStride === 0,
  ), viewerToken("--fc-ink-2", "#26343f"), "Fuselage axial x parameter lines");
  normalGeometry.dispose();
}

function jacobianDelta(displayUnit: string, jacobianUnit: string, delta: number): number | null {
  if (displayUnit === jacobianUnit) return delta;
  if (displayUnit === "deg" && jacobianUnit === "rad") return THREE.MathUtils.degToRad(delta);
  return null;
}

function nearlyEqual(left: number, right: number): boolean {
  return Math.abs(left - right) <= 1e-8 * Math.max(1, Math.abs(left), Math.abs(right));
}

export function mapLoomAnalyticTargetValue(
  asset: LoomNativeHybridAsset,
  parameterId: string,
  controlValue: number,
  acceptedRootChord?: number,
  acceptedTipChord?: number,
): number | null {
  if (!Number.isFinite(controlValue)) return null;
  if (parameterId !== "taper") return controlValue;
  const root = asset.header.parameters.definitions.find((parameter) => parameter.id === "chord_root");
  const taper = asset.header.parameters.definitions.find((parameter) => parameter.id === "taper");
  if (!root || !taper || !Number.isFinite(acceptedRootChord) || !Number.isFinite(acceptedTipChord)
      || acceptedRootChord! <= 0 || !nearlyEqual(acceptedRootChord!, root.value)
      || !nearlyEqual(acceptedTipChord! / acceptedRootChord!, taper.value)) return null;
  return controlValue / acceptedRootChord!;
}

export function deriveLoomAnalyticPreviewSurface(
  asset: LoomNativeHybridAsset,
  parameterId: string,
  targetValue: number,
): LoomAnalyticPreviewSurface | null {
  const { header } = asset;
  const parameterIndex = header.jacobian.parameterOrder.indexOf(parameterId);
  const parameter = header.parameters.definitions.find((candidate) => candidate.id === parameterId);
  const vertexCount = header.surface.vertexCount;
  const faceCount = header.surface.faceCount;
  const dynamicCount = header.jacobian.dynamicVertexCount;
  const parameterCount = header.jacobian.parameterOrder.length;
  const shape = header.jacobian.shape;
  if (!parameter || parameterIndex < 0 || !Number.isFinite(targetValue)) return null;
  if (!header.parameters.frozenTopology || !header.jacobian.validation.topologyStable) return null;
  if (!Number.isSafeInteger(vertexCount) || vertexCount <= 0
      || !Number.isSafeInteger(faceCount) || faceCount <= 0
      || !Number.isSafeInteger(dynamicCount) || dynamicCount <= 0) return null;
  if (asset.surface.positions.length !== vertexCount * 3
      || asset.surface.faces.length !== faceCount * 3
      || asset.jacobian.vertexIndices.length !== dynamicCount
      || asset.jacobian.values.length !== dynamicCount * 3 * parameterCount) return null;
  if (shape.length !== 3 || shape[0] !== dynamicCount || shape[1] !== 3 || shape[2] !== parameterCount) return null;
  if (!Number.isFinite(parameter.value) || !Number.isFinite(parameter.minimum)
      || !Number.isFinite(parameter.maximum) || parameter.maximum < parameter.minimum
      || targetValue < parameter.minimum || targetValue > parameter.maximum) return null;
  if (!asset.surface.positions.every(Number.isFinite) || !asset.jacobian.values.every(Number.isFinite)) return null;
  if (!asset.surface.faces.every((vertex) => vertex < vertexCount)) return null;

  const seenVertices = new Set<number>();
  for (const vertex of asset.jacobian.vertexIndices) {
    if (vertex >= vertexCount || seenVertices.has(vertex)) return null;
    seenVertices.add(vertex);
  }
  const deltaDisplay = targetValue - parameter.value;
  const deltaJacobian = jacobianDelta(parameter.displayUnit, parameter.jacobianUnit, deltaDisplay);
  if (deltaJacobian === null || !Number.isFinite(deltaJacobian)) return null;
  const positions = new Float32Array(asset.surface.positions);
  for (let row = 0; row < dynamicCount; row += 1) {
    const positionOffset = (asset.jacobian.vertexIndices[row] ?? 0) * 3;
    for (let axis = 0; axis < 3; axis += 1) {
      const jacobianOffset = (row * 3 + axis) * parameterCount + parameterIndex;
      positions[positionOffset + axis] = (asset.surface.positions[positionOffset + axis] ?? 0)
        + (asset.jacobian.values[jacobianOffset] ?? 0) * deltaJacobian;
    }
  }
  return {
    baseValue: parameter.value,
    deltaDisplay,
    deltaJacobian,
    faces: asset.surface.faces,
    fuselageSeamVertexIndices: asset.seam.fuselageVertexIndices,
    parameterId,
    positions,
    targetValue,
    wingSeamVertexIndices: asset.seam.wingVertexIndices,
  };
}

export function deriveLoomViewerDisplayPolicy(authoringPreview: boolean): LoomViewerDisplayPolicy {
  return authoringPreview
    ? { displayMode: "edited-preview", showAcceptedExtraction: false, staleExtractionVisible: false }
    : { displayMode: "accepted-extraction", showAcceptedExtraction: true, staleExtractionVisible: false };
}

export function addSymmetricTrailingEdgeLayer(
  group: THREE.Group,
  asset: LoomNativeHybridAsset,
  color: string,
): number {
  const values: number[] = [];
  for (let edge = 0; edge < asset.panel.trailingEdgeEdges.length; edge += 2) {
    const start = (asset.panel.trailingEdgeEdges[edge] ?? 0) * 3;
    const end = (asset.panel.trailingEdgeEdges[edge + 1] ?? 0) * 3;
    if (start + 2 >= asset.panel.positions.length || end + 2 >= asset.panel.positions.length) continue;
    const a = Array.from(asset.panel.positions.subarray(start, start + 3));
    const b = Array.from(asset.panel.positions.subarray(end, end + 3));
    values.push(...a, ...b, a[0] ?? 0, -(a[1] ?? 0), a[2] ?? 0, b[0] ?? 0, -(b[1] ?? 0), b[2] ?? 0);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(values, 3));
  const lines = new THREE.LineSegments(geometry, new THREE.LineBasicMaterial({ color, depthTest: true }));
  lines.name = "Reflected source-fixture smart trailing-edge chains";
  lines.renderOrder = 9;
  group.add(lines);
  return values.length / 6;
}

export function addSymmetricJacobianLayer(
  group: THREE.Group,
  asset: LoomNativeHybridAsset,
  parameterId: string,
  scale: number,
  color: string,
): number {
  const parameterIndex = asset.header.jacobian.parameterOrder.indexOf(parameterId);
  if (parameterIndex < 0) return 0;
  const parameterCount = asset.header.jacobian.parameterOrder.length;
  const rowCount = asset.jacobian.vertexIndices.length;
  const stride = Math.max(1, Math.ceil(rowCount / 240));
  const values: number[] = [];
  for (let row = 0; row < rowCount; row += stride) {
    const vertex = asset.jacobian.vertexIndices[row] ?? 0;
    const positionOffset = vertex * 3;
    if (positionOffset + 2 >= asset.surface.positions.length) continue;
    const x = asset.surface.positions[positionOffset] ?? 0;
    const y = asset.surface.positions[positionOffset + 1] ?? 0;
    const z = asset.surface.positions[positionOffset + 2] ?? 0;
    if (y < -1e-7) continue;
    const dx = asset.jacobian.values[(row * 3) * parameterCount + parameterIndex] ?? 0;
    const dy = asset.jacobian.values[(row * 3 + 1) * parameterCount + parameterIndex] ?? 0;
    const dz = asset.jacobian.values[(row * 3 + 2) * parameterCount + parameterIndex] ?? 0;
    values.push(x, y, z, x + dx * scale, y + dy * scale, z + dz * scale);
    if (y > 1e-7) values.push(x, -y, z, x + dx * scale, -y - dy * scale, z + dz * scale);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(values, 3));
  const vectors = new THREE.LineSegments(
    geometry,
    new THREE.LineBasicMaterial({ color, depthTest: false }),
  );
  vectors.name = `Reflected source-fixture analytic dV/d${parameterId}`;
  vectors.renderOrder = 10;
  group.add(vectors);
  return values.length / 6;
}

export function fitLoomCameraToBounds(
  camera: THREE.PerspectiveCamera,
  controls: { target: THREE.Vector3; update: () => void },
  bounds: THREE.Box3,
): void {
  const center = bounds.getCenter(new THREE.Vector3());
  const size = bounds.getSize(new THREE.Vector3());
  const radius = Math.max(size.x, size.y, size.z, 1);
  controls.target.copy(center);
  camera.position.set(center.x - radius * 0.15, center.y - radius * 0.35, center.z + radius * 1.62);
  camera.near = radius / 1000;
  camera.far = radius * 100;
  camera.updateProjectionMatrix();
  controls.update();
}

export function authoringGeometrySignature(scene: THREE.Object3D): string {
  const point = new THREE.Vector3();
  let count = 0;
  let sumX = 0;
  let sumY = 0;
  let sumZ = 0;
  let weighted = 0;
  scene.traverse((object) => {
    const mesh = object as THREE.Mesh;
    const positions = mesh.geometry?.getAttribute("position");
    if (!mesh.isMesh || !positions) return;
    for (let index = 0; index < positions.count; index += 1) {
      point.fromBufferAttribute(positions, index).applyMatrix4(mesh.matrixWorld);
      count += 1;
      sumX += point.x;
      sumY += point.y;
      sumZ += point.z;
      weighted += (point.x * 3 + point.y * 5 + point.z * 7) * ((count % 97) + 1);
    }
  });
  return [count, sumX, sumY, sumZ, weighted]
    .map((value, index) => index === 0 ? String(value) : value.toFixed(5))
    .join(":");
}
